import React, { useState, useEffect, useRef } from 'react';
import { X, Loader2, AlertCircle, Plus, Trash2, ChevronUp, ChevronDown, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_TYPE_GROUPS = [
  { label: 'United States', options: [
    { value: 'llc_formation', label: 'LLC Formation' },
    { value: 'c_corp_formation', label: 'C-Corp Formation' },
    { value: 's_corp_formation', label: 'S-Corp Formation' },
    { value: 'ein_application', label: 'EIN Application' },
    { value: 'registered_agent', label: 'Registered Agent' },
    { value: 'mail_forwarding', label: 'Mail Forwarding' },
    { value: 'amendment', label: 'State Filings & Amendments' },
  ]},
  { label: 'Canada', options: [
    { value: 'ca_federal_incorporation', label: 'Federal Corporate Incorporation' },
    { value: 'ca_extra_provincial_registration', label: 'Extra-Provincial Registration' },
    { value: 'ca_federal_annual_return', label: 'Federal Annual Return' },
    { value: 'ca_provincial_annual_return', label: 'Provincial Annual Return' },
    { value: 'ca_t2_tax_return', label: 'Corporate Tax Return (T2)' },
    { value: 'ca_t2_account_activation', label: 'T2 Account Activation' },
    { value: 'ca_schedule_91', label: 'Schedule 91 (Treaty Disclosure)' },
    { value: 'ca_gst_hst_registration', label: 'GST/HST Registration' },
    { value: 'ca_gst_hst_return', label: 'GST/HST Sales Tax Return' },
  ]},
  { label: 'United Kingdom', options: [
    { value: 'uk_ltd_registration', label: 'Ltd Company Registration' },
    { value: 'uk_confirmation_statement', label: 'Confirmation Statement (CS01)' },
    { value: 'uk_eccta_id_verification', label: 'ECCTA Biometric ID Verification' },
    { value: 'uk_ct600_tax_return', label: 'Corporation Tax (CT600)' },
    { value: 'uk_hmrc_ct_registration', label: 'HMRC Corporate Tax Registration' },
    { value: 'uk_vat_registration', label: 'VAT Registration & Filings' },
    { value: 'uk_vat_registration_nonresident', label: 'VAT Registration (Non-Establishment)' },
  ]},
  { label: 'Singapore', options: [
    { value: 'sg_pte_ltd_incorporation', label: 'Pte. Ltd. Incorporation' },
    { value: 'sg_acra_annual_return', label: 'ACRA Annual Return' },
    { value: 'sg_corporate_secretary', label: 'Corporate Secretary Service' },
    { value: 'sg_nominee_director', label: 'Nominee Director Service' },
    { value: 'sg_corporate_tax_return', label: 'Corporate Tax (Form C-S / C)' },
    { value: 'sg_eci_filing', label: 'ECI Filing' },
    { value: 'sg_gst_registration', label: 'GST Registration Setup' },
    { value: 'sg_iras_corppass_config', label: 'IRAS CorpPass Configuration' },
  ]},
  { label: 'United Arab Emirates', options: [
    { value: 'ae_free_zone_license_setup', label: 'Free Zone License Setup' },
    { value: 'ae_trade_license_renewal', label: 'Trade License Renewal' },
    { value: 'ae_aml_ubo_reporting', label: 'AML / UBO Reporting' },
    { value: 'ae_corporate_tax_return', label: 'Corporate Tax Return' },
    { value: 'ae_emaratax_ct_registration', label: 'EmaraTax Corporate Tax Registration' },
    { value: 'ae_esr_filing', label: 'ESR Filing' },
    { value: 'ae_vat_registration', label: 'VAT Registration' },
  ]},
];

const SERVICE_TYPES = SERVICE_TYPE_GROUPS.flatMap(g => g.options);

const FREQUENCIES = [
  { value: 'one_time', label: 'One Time' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'duration_based', label: 'Duration Based' },
];

const AMENDMENT_SUB_TYPES = [
  { value: 'name_change', label: 'Name Change' },
  { value: 'address_change', label: 'Address Change' },
  { value: 'registered_agent_change', label: 'Registered Agent Change' },
  { value: 'director_member_change', label: 'Director/Member Change' },
  { value: 'share_structure_change', label: 'Share Structure Change' },
  { value: 'certificate_good_standing', label: 'Certificate of Good Standing' },
  { value: 'franchise_tax', label: 'Franchise Tax Filing' },
  { value: 'annual_report', label: 'Annual Report' },
  { value: 'foreign_qualification', label: 'Foreign Qualification' },
  { value: 'dissolution', label: 'Dissolution' },
];

const AMENDMENT_ENTITY_TYPES = [
  { value: 'llc', label: 'LLC' },
  { value: 'c_corp', label: 'C-Corp' },
  { value: 's_corp', label: 'S-Corp' },
];

const FIELD_TYPES = [
  { value: 'text', label: 'Short Text' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'date', label: 'Date' },
  { value: 'boolean', label: 'Yes / No' },
];

const US_STATES_OPTIONS = 'Alabama,Alaska,Arizona,Arkansas,California,Colorado,Connecticut,Delaware,Florida,Georgia,Hawaii,Idaho,Illinois,Indiana,Iowa,Kansas,Kentucky,Louisiana,Maine,Maryland,Massachusetts,Michigan,Minnesota,Mississippi,Missouri,Montana,Nebraska,Nevada,New Hampshire,New Jersey,New Mexico,New York,North Carolina,North Dakota,Ohio,Oklahoma,Oregon,Pennsylvania,Rhode Island,South Carolina,South Dakota,Tennessee,Texas,Utah,Vermont,Virginia,Washington,West Virginia,Wisconsin,Wyoming';

// Default information fields per service type
const DEFAULT_INFO_FIELDS = {
  llc_formation: [
    { field_key: 'company_name', label: 'Company Name', field_type: 'text', required: true, options: [], order: 1 },
    { field_key: 'state_of_formation', label: 'State of Formation', field_type: 'dropdown', required: true, options: US_STATES_OPTIONS.split(','), order: 2 },
    { field_key: 'member_full_name', label: 'Member Full Name', field_type: 'text', required: true, options: [], order: 3 },
    { field_key: 'member_email', label: 'Member Email', field_type: 'text', required: true, options: [], order: 4 },
    { field_key: 'member_ownership_percent', label: 'Member Ownership %', field_type: 'text', required: true, options: [], order: 5 },
  ],
  c_corp_formation: [
    { field_key: 'company_name', label: 'Company Name', field_type: 'text', required: true, options: [], order: 1 },
    { field_key: 'state_of_formation', label: 'State of Formation', field_type: 'dropdown', required: true, options: US_STATES_OPTIONS.split(','), order: 2 },
    { field_key: 'director_full_name', label: 'Director Full Name', field_type: 'text', required: true, options: [], order: 3 },
    { field_key: 'director_email', label: 'Director Email', field_type: 'text', required: true, options: [], order: 4 },
    { field_key: 'share_structure', label: 'Share Structure', field_type: 'textarea', required: false, options: [], order: 5 },
  ],
  s_corp_formation: [
    { field_key: 'company_name', label: 'Company Name', field_type: 'text', required: true, options: [], order: 1 },
    { field_key: 'state_of_formation', label: 'State of Formation', field_type: 'dropdown', required: true, options: US_STATES_OPTIONS.split(','), order: 2 },
    { field_key: 'shareholder_full_name', label: 'Shareholder Full Name', field_type: 'text', required: true, options: [], order: 3 },
    { field_key: 'shareholder_info', label: 'Shareholder Info', field_type: 'textarea', required: false, options: [], order: 4 },
  ],
  ein_application: [
    { field_key: 'responsible_party_name', label: 'Responsible Party Name', field_type: 'text', required: true, options: [], order: 1 },
    { field_key: 'responsible_party_ssn_itin', label: 'Responsible Party SSN/ITIN', field_type: 'text', required: true, options: [], order: 2 },
    { field_key: 'business_purpose', label: 'Business Purpose', field_type: 'textarea', required: false, options: [], order: 3 },
  ],
  registered_agent: [
    { field_key: 'company_name', label: 'Company Name', field_type: 'text', required: true, options: [], order: 1 },
    { field_key: 'current_state', label: 'Current State', field_type: 'dropdown', required: true, options: US_STATES_OPTIONS.split(','), order: 2 },
  ],
  mail_forwarding: [
    { field_key: 'full_name', label: 'Full Name', field_type: 'text', required: true, options: [], order: 1 },
    { field_key: 'physical_address', label: 'Physical Address', field_type: 'textarea', required: true, options: [], order: 2 },
  ],
  amendment: [
    { field_key: 'company_name', label: 'Company Name', field_type: 'text', required: true, options: [], order: 1 },
    { field_key: 'amendment_type', label: 'Amendment Type', field_type: 'dropdown', required: true, options: ['Name Change', 'Address Change', 'RA Change', 'Director Change', 'Share Change', 'Good Standing', 'Franchise Tax', 'Annual Report', 'Foreign Qualification', 'Dissolution'], order: 2 },
    { field_key: 'description_of_changes', label: 'Description of Changes', field_type: 'textarea', required: true, options: [], order: 3 },
  ],
};

// Default document requirements per service type
const DEFAULT_DOC_REQUIREMENTS = {
  llc_formation: [
    { doc_key: 'passport_copy', name: 'Passport Copy', description: 'Clear color copy of passport bio page', required: true },
    { doc_key: 'proof_of_address', name: 'Proof of Address', description: 'Utility bill or bank statement, last 3 months', required: true },
  ],
  c_corp_formation: [
    { doc_key: 'passport_copy', name: 'Passport Copy', description: 'Clear color copy of passport bio page', required: true },
    { doc_key: 'proof_of_address', name: 'Proof of Address', description: 'Utility bill or bank statement, last 3 months', required: true },
    { doc_key: 'director_id', name: 'Director ID', description: 'Government-issued ID for each director', required: true },
  ],
  s_corp_formation: [
    { doc_key: 'passport_copy', name: 'Passport Copy', description: 'Clear color copy of passport bio page', required: true },
    { doc_key: 'proof_of_address', name: 'Proof of Address', description: 'Utility bill or bank statement, last 3 months', required: true },
  ],
  ein_application: [
    { doc_key: 'formation_documents', name: 'Formation Documents', description: 'Articles of Organization or Incorporation', required: true },
    { doc_key: 'ssn_itin_document', name: 'SSN/ITIN Document', description: 'SSN or ITIN letter for responsible party', required: true },
  ],
  registered_agent: [
    { doc_key: 'formation_documents', name: 'Formation Documents', description: 'Current Articles of Organization', required: true },
  ],
  mail_forwarding: [
    { doc_key: 'passport_copy', name: 'Passport Copy', description: 'Clear color copy of passport bio page', required: true },
    { doc_key: 'usps_form_1583', name: 'USPS Form 1583', description: 'Signed and notarized USPS Form 1583', required: true },
  ],
  amendment: [
    { doc_key: 'current_formation_documents', name: 'Current Formation Documents', description: 'Existing Articles of Organization', required: true },
    { doc_key: 'amendment_details', name: 'Amendment Details', description: 'Written description of changes needed', required: true },
  ],
};

// Helper to generate key from label
const generateKey = (label) => {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
};

const ProductDrawer = ({ isOpen, onClose, product, token, onSuccess }) => {
  const isEdit = !!product;
  const previousServiceType = useRef(null);
  
  const [activeTab, setActiveTab] = useState(1);
  const [formData, setFormData] = useState({
    product_name: '',
    service_type: 'llc_formation',
    description: '',
    platform_fees: '',
    has_state_fee: true,
    frequency: 'one_time',
    is_active: true,
    amendment_sub_type: '',
    amendment_entity_type: '',
  });
  
  const [informationFields, setInformationFields] = useState([]);
  const [documentRequirements, setDocumentRequirements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  // Show toast
  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(''), 3000);
  };

  // Reset form when drawer opens/closes or product changes
  useEffect(() => {
    if (isOpen) {
      if (product) {
        setFormData({
          product_name: product.product_name || '',
          service_type: product.service_type || 'llc_formation',
          description: product.description || '',
          platform_fees: product.platform_fees?.toString() || '',
          has_state_fee: product.govt_fees > 0 || product.has_state_fee || false,
          frequency: product.frequency_settings?.frequency || 'one_time',
          is_active: product.is_active !== false,
          amendment_sub_type: product.amendment_sub_type || '',
          amendment_entity_type: product.amendment_entity_type || '',
        });
        
        const existingInfoFields = product.information_fields || [];
        const existingDocReqs = product.document_requirements || [];
        
        // If editing and has existing data, use it; otherwise auto-populate
        if (existingInfoFields.length > 0) {
          setInformationFields(existingInfoFields);
        } else {
          setInformationFields(DEFAULT_INFO_FIELDS[product.service_type] || []);
        }
        
        if (existingDocReqs.length > 0) {
          setDocumentRequirements(existingDocReqs);
        } else {
          setDocumentRequirements(DEFAULT_DOC_REQUIREMENTS[product.service_type] || []);
        }
        
        previousServiceType.current = product.service_type;
      } else {
        // New product - set defaults
        const defaultServiceType = 'llc_formation';
        setFormData({
          product_name: '',
          service_type: defaultServiceType,
          description: '',
          platform_fees: '',
          has_state_fee: true,
          frequency: 'one_time',
          is_active: true,
          amendment_sub_type: '',
          amendment_entity_type: '',
        });
        setInformationFields(DEFAULT_INFO_FIELDS[defaultServiceType] || []);
        setDocumentRequirements(DEFAULT_DOC_REQUIREMENTS[defaultServiceType] || []);
        previousServiceType.current = defaultServiceType;
      }
      setActiveTab(1);
      setError('');
    }
  }, [isOpen, product]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle service_type change specially
    if (name === 'service_type') {
      const newServiceType = value;
      const oldServiceType = previousServiceType.current;
      
      // Clear amendment fields if changing away from amendment
      const newFormData = {
        ...formData,
        service_type: newServiceType,
        amendment_sub_type: newServiceType !== 'amendment' ? '' : formData.amendment_sub_type,
        amendment_entity_type: newServiceType !== 'amendment' ? '' : formData.amendment_entity_type,
      };
      setFormData(newFormData);
      
      // Auto-populate fields if service type changed
      if (newServiceType !== oldServiceType) {
        // Check if we should auto-populate (only if current arrays are empty or came from defaults)
        const existingProductInfoFields = product?.information_fields || [];
        const existingProductDocReqs = product?.document_requirements || [];
        
        // Auto-populate if: no existing saved data for this product
        if (existingProductInfoFields.length === 0) {
          setInformationFields(DEFAULT_INFO_FIELDS[newServiceType] || []);
        }
        if (existingProductDocReqs.length === 0) {
          setDocumentRequirements(DEFAULT_DOC_REQUIREMENTS[newServiceType] || []);
        }
        
        // Show toast if we auto-populated
        if (existingProductInfoFields.length === 0 || existingProductDocReqs.length === 0) {
          const serviceLabel = SERVICE_TYPES.find(s => s.value === newServiceType)?.label || newServiceType;
          showToast(`Default fields loaded for ${serviceLabel}. You can customise them.`);
        }
        
        previousServiceType.current = newServiceType;
      }
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Information Fields handlers
  const handleAddField = () => {
    const newOrder = informationFields.length + 1;
    setInformationFields([...informationFields, {
      field_key: '',
      label: '',
      field_type: 'text',
      required: true,
      options: [],
      order: newOrder,
    }]);
  };

  const handleRemoveField = (index) => {
    const updated = informationFields.filter((_, i) => i !== index);
    // Recalculate order
    updated.forEach((f, i) => f.order = i + 1);
    setInformationFields(updated);
  };

  const handleFieldChange = (index, field, value) => {
    const updated = [...informationFields];
    updated[index][field] = value;
    
    // Auto-generate key when label changes
    if (field === 'label' && value) {
      updated[index].field_key = generateKey(value);
    }
    
    setInformationFields(updated);
  };

  const handleMoveField = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= informationFields.length) return;
    
    const updated = [...informationFields];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    // Recalculate order
    updated.forEach((f, i) => f.order = i + 1);
    setInformationFields(updated);
  };

  // Document Requirements handlers
  const handleAddDocument = () => {
    setDocumentRequirements([...documentRequirements, {
      doc_key: '',
      name: '',
      description: '',
      required: true,
    }]);
  };

  const handleRemoveDocument = (index) => {
    setDocumentRequirements(documentRequirements.filter((_, i) => i !== index));
  };

  const handleDocumentChange = (index, field, value) => {
    const updated = [...documentRequirements];
    updated[index][field] = value;
    
    // Auto-generate key when name changes
    if (field === 'name' && value) {
      updated[index].doc_key = generateKey(value);
    }
    
    setDocumentRequirements(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation - Tab 1
    if (!formData.product_name.trim()) {
      setError('Product name is required');
      setActiveTab(1);
      return;
    }
    if (!formData.platform_fees || parseFloat(formData.platform_fees) < 0) {
      setError('Valid fee amount is required');
      setActiveTab(1);
      return;
    }
    
    // Amendment-specific validation
    if (formData.service_type === 'amendment') {
      if (!formData.amendment_sub_type) {
        setError('Amendment Sub-Type is required for State Filings & Amendments');
        setActiveTab(1);
        return;
      }
      if (!formData.amendment_entity_type) {
        setError('Entity Type is required for State Filings & Amendments');
        setActiveTab(1);
        return;
      }
    }

    // Validation - Tab 2 (info fields must have labels)
    for (let i = 0; i < informationFields.length; i++) {
      if (!informationFields[i].label.trim()) {
        setError(`Information field ${i + 1} needs a label`);
        setActiveTab(2);
        return;
      }
    }

    // Validation - Tab 3 (documents must have names)
    for (let i = 0; i < documentRequirements.length; i++) {
      if (!documentRequirements[i].name.trim()) {
        setError(`Document ${i + 1} needs a name`);
        setActiveTab(3);
        return;
      }
    }

    setLoading(true);

    try {
      const payload = {
        product_name: formData.product_name.trim(),
        service_type: formData.service_type,
        state: 'All States',  // Default - can be made configurable later
        applicable_entity_types: ['us_llc', 'us_c_corp', 'us_s_corp'],  // Default - can be made configurable later
        description: formData.description.trim() || null,
        platform_fees: parseFloat(formData.platform_fees),
        govt_fees: formData.has_state_fee ? 0 : 0,
        has_state_fee: formData.has_state_fee,
        frequency_settings: {
          frequency: formData.frequency,
          annual_trigger_month: 1,
          annual_trigger_day: 1,
          quarterly_months: [1, 4, 7, 10]
        },
        is_active: formData.is_active,
        amendment_sub_type: formData.service_type === 'amendment' ? formData.amendment_sub_type : null,
        amendment_entity_type: formData.service_type === 'amendment' ? formData.amendment_entity_type : null,
        information_fields: informationFields.map(f => ({
          ...f,
          field_key: f.field_key || generateKey(f.label),
        })),
        document_requirements: documentRequirements.map(d => ({
          ...d,
          doc_key: d.doc_key || generateKey(d.name),
        })),
      };

      if (isEdit) {
        await axios.put(`${API}/admin/products/${product.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/admin/products`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      onSuccess(isEdit ? 'Product updated successfully' : 'Product created successfully');
      onClose();
    } catch (err) {
      // Handle Pydantic validation errors (detail is array of objects) or string errors
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        // Pydantic validation error - extract first error message
        const firstError = detail[0];
        const errorMsg = firstError?.msg || firstError?.message || 'Validation error';
        setError(errorMsg);
      } else if (typeof detail === 'string') {
        setError(detail);
      } else if (typeof detail === 'object' && detail?.msg) {
        setError(detail.msg);
      } else {
        setError('Failed to save product');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
      />
      
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-[60] flex items-center gap-2 px-4 py-3 bg-[#00E87A] text-black rounded-lg shadow-lg">
          <CheckCircle className="w-5 h-5" />
          {toast}
        </div>
      )}
      
      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-lg bg-[#161B22] border-l border-gray-800 z-50 flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 border-b border-gray-800 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">
            {isEdit ? 'Edit Product' : 'Add Product'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex-shrink-0 border-b border-gray-800 px-6">
          <div className="flex gap-6">
            {[
              { id: 1, label: 'Basic Info' },
              { id: 2, label: 'Information Fields' },
              { id: 3, label: 'Documents' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'text-[#00E87A] border-[#00E87A]'
                    : 'text-gray-400 border-transparent hover:text-white'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Form Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          <form id="product-form" onSubmit={handleSubmit} className="space-y-5">
            {/* Error */}
            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Tab 1: Basic Info */}
            {activeTab === 1 && (
              <>
                {/* Product Name */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    Product Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="product_name"
                    value={formData.product_name}
                    onChange={handleChange}
                    placeholder="e.g., Wyoming LLC Formation"
                    className="w-full px-4 py-2.5 bg-[#0D1117] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#00E87A]"
                    data-testid="product-name-input"
                  />
                </div>

                {/* Service Type */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    Service Type <span className="text-red-400">*</span>
                  </label>
                  <select
                    name="service_type"
                    value={formData.service_type}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-[#0D1117] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#00E87A]"
                    data-testid="service-type-select"
                  >
                    {SERVICE_TYPE_GROUPS.map(group => (
                      <optgroup key={group.label} label={group.label}>
                        {group.options.map(type => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>

                {/* Amendment Sub-Type */}
                {formData.service_type === 'amendment' && (
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">
                      Amendment Sub-Type <span className="text-red-400">*</span>
                    </label>
                    <select
                      name="amendment_sub_type"
                      value={formData.amendment_sub_type}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 bg-[#0D1117] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#00E87A]"
                      data-testid="amendment-sub-type-select"
                    >
                      <option value="">Select sub-type...</option>
                      {AMENDMENT_SUB_TYPES.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Amendment Entity Type */}
                {formData.service_type === 'amendment' && (
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">
                      Applies to Entity Type <span className="text-red-400">*</span>
                    </label>
                    <select
                      name="amendment_entity_type"
                      value={formData.amendment_entity_type}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 bg-[#0D1117] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#00E87A]"
                      data-testid="amendment-entity-type-select"
                    >
                      <option value="">Select entity type...</option>
                      {AMENDMENT_ENTITY_TYPES.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Description */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Optional product description..."
                    className="w-full px-4 py-2.5 bg-[#0D1117] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#00E87A] resize-none"
                    data-testid="description-input"
                  />
                </div>

                {/* Our Fee */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    Our Fee (USD) <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      name="platform_fees"
                      value={formData.platform_fees}
                      onChange={handleChange}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      className="w-full pl-8 pr-4 py-2.5 bg-[#0D1117] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#00E87A]"
                      data-testid="platform-fees-input"
                    />
                  </div>
                </div>

                {/* Has State Fee Toggle */}
                <div className="flex items-center justify-between p-4 bg-[#0D1117] rounded-lg">
                  <div>
                    <p className="text-white font-medium">Has State Fee</p>
                    <p className="text-gray-500 text-sm">State fee pulled from state_fees at order time</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="has_state_fee"
                      checked={formData.has_state_fee}
                      onChange={handleChange}
                      className="sr-only peer"
                      data-testid="has-state-fee-toggle"
                    />
                    <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#00E87A]"></div>
                  </label>
                </div>

                {/* Frequency */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    Frequency <span className="text-red-400">*</span>
                  </label>
                  <select
                    name="frequency"
                    value={formData.frequency}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-[#0D1117] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#00E87A]"
                    data-testid="frequency-select"
                  >
                    {FREQUENCIES.map(freq => (
                      <option key={freq.value} value={freq.value}>{freq.label}</option>
                    ))}
                  </select>
                </div>

                {/* Is Active Toggle */}
                <div className="flex items-center justify-between p-4 bg-[#0D1117] rounded-lg">
                  <div>
                    <p className="text-white font-medium">Active</p>
                    <p className="text-gray-500 text-sm">Product is available for new orders</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleChange}
                      className="sr-only peer"
                      data-testid="is-active-toggle"
                    />
                    <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#00E87A]"></div>
                  </label>
                </div>
              </>
            )}

            {/* Tab 2: Information Fields */}
            {activeTab === 2 && (
              <>
                <p className="text-gray-400 text-sm mb-4">
                  Define the information clients must provide when ordering this product.
                </p>

                {informationFields.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No fields defined. Click "Add Field" to start.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {informationFields.map((field, index) => (
                      <div key={index} className="p-4 bg-[#0D1117] border border-gray-700 rounded-lg space-y-3">
                        <div className="flex items-start gap-2">
                          {/* Reorder buttons */}
                          <div className="flex flex-col gap-1 pt-1">
                            <button
                              type="button"
                              onClick={() => handleMoveField(index, 'up')}
                              disabled={index === 0}
                              className="p-1 text-gray-500 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                              <ChevronUp className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleMoveField(index, 'down')}
                              disabled={index === informationFields.length - 1}
                              className="p-1 text-gray-500 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                              <ChevronDown className="w-4 h-4" />
                            </button>
                          </div>

                          {/* Field content */}
                          <div className="flex-1 space-y-3">
                            {/* Label */}
                            <input
                              type="text"
                              value={field.label}
                              onChange={(e) => handleFieldChange(index, 'label', e.target.value)}
                              placeholder="Field Label"
                              className="w-full px-3 py-2 bg-[#161B22] border border-gray-700 rounded text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#00E87A]"
                            />

                            <div className="flex gap-3">
                              {/* Field Type */}
                              <select
                                value={field.field_type}
                                onChange={(e) => handleFieldChange(index, 'field_type', e.target.value)}
                                className="flex-1 px-3 py-2 bg-[#161B22] border border-gray-700 rounded text-white text-sm focus:outline-none focus:border-[#00E87A]"
                              >
                                {FIELD_TYPES.map(ft => (
                                  <option key={ft.value} value={ft.value}>{ft.label}</option>
                                ))}
                              </select>

                              {/* Required toggle */}
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={field.required}
                                  onChange={(e) => handleFieldChange(index, 'required', e.target.checked)}
                                  className="sr-only peer"
                                />
                                <div className={`w-9 h-5 rounded-full transition-colors ${field.required ? 'bg-[#00E87A]' : 'bg-gray-700'} relative`}>
                                  <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${field.required ? 'left-[18px]' : 'left-0.5'}`}></div>
                                </div>
                                <span className="text-gray-400 text-sm">Required</span>
                              </label>
                            </div>

                            {/* Options (for dropdown type) */}
                            {field.field_type === 'dropdown' && (
                              <div>
                                <input
                                  type="text"
                                  value={Array.isArray(field.options) ? field.options.join(', ') : ''}
                                  onChange={(e) => handleFieldChange(index, 'options', e.target.value.split(',').map(o => o.trim()).filter(Boolean))}
                                  placeholder="Options (comma separated, e.g. Option 1, Option 2)"
                                  className="w-full px-3 py-2 bg-[#161B22] border border-gray-700 rounded text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#00E87A]"
                                />
                              </div>
                            )}
                          </div>

                          {/* Remove button */}
                          <button
                            type="button"
                            onClick={() => handleRemoveField(index)}
                            className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Field Button */}
                <button
                  type="button"
                  onClick={handleAddField}
                  className="w-full py-2.5 border border-gray-700 rounded-lg text-gray-400 hover:text-white hover:border-gray-600 flex items-center justify-center gap-2 transition-colors"
                  data-testid="add-field-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add Field
                </button>
              </>
            )}

            {/* Tab 3: Document Requirements */}
            {activeTab === 3 && (
              <>
                <p className="text-gray-400 text-sm mb-4">
                  Define the documents clients must upload when ordering this product.
                </p>

                {documentRequirements.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No documents required. Click "Add Document" to start.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {documentRequirements.map((doc, index) => (
                      <div key={index} className="p-4 bg-[#0D1117] border border-gray-700 rounded-lg space-y-3">
                        <div className="flex items-start gap-3">
                          {/* Document content */}
                          <div className="flex-1 space-y-3">
                            {/* Name */}
                            <input
                              type="text"
                              value={doc.name}
                              onChange={(e) => handleDocumentChange(index, 'name', e.target.value)}
                              placeholder="Document Name (e.g., Passport Copy)"
                              className="w-full px-3 py-2 bg-[#161B22] border border-gray-700 rounded text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#00E87A]"
                            />

                            {/* Description */}
                            <input
                              type="text"
                              value={doc.description}
                              onChange={(e) => handleDocumentChange(index, 'description', e.target.value)}
                              placeholder="Description (e.g., Clear color copy of bio page)"
                              className="w-full px-3 py-2 bg-[#161B22] border border-gray-700 rounded text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#00E87A]"
                            />

                            {/* Required toggle */}
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={doc.required}
                                onChange={(e) => handleDocumentChange(index, 'required', e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className={`w-9 h-5 rounded-full transition-colors ${doc.required ? 'bg-[#00E87A]' : 'bg-gray-700'} relative`}>
                                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${doc.required ? 'left-[18px]' : 'left-0.5'}`}></div>
                              </div>
                              <span className="text-gray-400 text-sm">Required</span>
                            </label>
                          </div>

                          {/* Remove button */}
                          <button
                            type="button"
                            onClick={() => handleRemoveDocument(index)}
                            className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Document Button */}
                <button
                  type="button"
                  onClick={handleAddDocument}
                  className="w-full py-2.5 border border-gray-700 rounded-lg text-gray-400 hover:text-white hover:border-gray-600 flex items-center justify-center gap-2 transition-colors"
                  data-testid="add-document-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add Document
                </button>
              </>
            )}
          </form>
        </div>

        {/* Footer with Submit Button */}
        <div className="flex-shrink-0 border-t border-gray-800 p-6">
          <button
            type="submit"
            form="product-form"
            disabled={loading}
            className="w-full py-3 rounded-lg font-medium text-black disabled:opacity-50 flex items-center justify-center gap-2 bg-[#00E87A] hover:bg-[#00D46E] transition-colors"
            data-testid="submit-product-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Saving...
              </>
            ) : (
              isEdit ? 'Update Product' : 'Create Product'
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default ProductDrawer;
